import { CATEGORIES_TO_HIDE_PER_CUSTOMER } from "../../../../constants/customers";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useGetPageFromPath } from "utils/customHooks/genericHooks";
import type { PnlChartSectionType, TimeRangeFinite } from "types/types";
import PnlChartColumns, {
  PnlChartDataGetters,
} from "./PnlChartColumns/PnlChartColumns";
import { camelCase } from "lodash";
import { useHiddenCategoriesForOtherProcessesPnlChart } from "../pnlSummaryChartUtils";

type PnlSummaryChartContentProps = {
  pnlBreakdownChartCategorySectionsArr: PnlChartSectionType[];
  timeRange: TimeRangeFinite | null;
  isSubordinateProcessesOnly?: boolean;
  isInsideMachineBreakdownChart?: boolean;
  disableAnimation?: boolean;
};

// tslint:disable-next-line: cognitive-complexity
export function PnlSummaryChartContent({
  pnlBreakdownChartCategorySectionsArr,
  timeRange,
  isSubordinateProcessesOnly = false,
  isInsideMachineBreakdownChart = false,
  disableAnimation = false,
}: PnlSummaryChartContentProps) {
  const { shouldFilterCategories, categoriesToHide } = usePnlCategoriesToHide(
    timeRange,
    isSubordinateProcessesOnly
  );

  const pnlSectionsDisplay = shouldFilterCategories
    ? pnlBreakdownChartCategorySectionsArr.filter(({ title }) => {
        return !categoriesToHide.includes(camelCase(title));
      })
    : pnlBreakdownChartCategorySectionsArr;

  return (
    <>
      <PnlChartDataGetters {...{ timeRange }} />
      <PnlChartColumns
        {...{
          pnlSectionsDisplay,
          timeRange,
          isInsideMachineBreakdownChart,
          isSubordinateProcessesOnly,
          disableAnimation,
        }}
      />
    </>
  );
}

function usePnlCategoriesToHide(
  timeRange: TimeRangeFinite | null,
  isSubordinateProcessesOnly: boolean
) {
  const { isTrendsPage } = useGetPageFromPath();
  const ckey = useCustomerKey();
  const hiddenCategoriesForOtherProcessesPnlChart =
    useHiddenCategoriesForOtherProcessesPnlChart();

  const customersWhoHidePnlCategories = Object.keys(
    CATEGORIES_TO_HIDE_PER_CUSTOMER
  );

  const hiddenCategoriesForCustomer =
    CATEGORIES_TO_HIDE_PER_CUSTOMER[ckey] || [];
  const pnlCategoriesToHide = isSubordinateProcessesOnly
    ? hiddenCategoriesForOtherProcessesPnlChart
    : hiddenCategoriesForCustomer;

  const categoriesToHide = [...pnlCategoriesToHide];

  const shouldFilterCategories =
    (customersWhoHidePnlCategories.includes(ckey) ||
      isSubordinateProcessesOnly) &&
    !isTrendsPage; // don't filter out categories on trends page

  return { shouldFilterCategories, categoriesToHide };
}
